import { Skeleton } from "antd";
import { type MouseEvent, useContext } from "react";

import { BTText, type TBTStatusBadgeDisplayTypes } from "@btnet/core-components";
import { mobile } from "@btnet/utilities-webview";

import { BdsLink, BdsText } from "@buildertrend/components";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { routes } from "utilities/routes";

import { BTBreadcrumb } from "commonComponents/btWrappers/BTBreadcrumb/BTBreadcrumb";
import { BTItemHeader } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader";
import { type TBTItemHeaderIcon } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader.types";
import { DetailFrame } from "commonComponents/btWrappers/DetailFrame/DetailFrame";
import {
    canShare,
    EntityCopyLinkModalContent,
    type ISharingConfig,
} from "commonComponents/entity/sharing/EntityShareButton/EntityShareButton";

import { useSelectionOptionHeaderActions } from "./SelectionOptionHeaderActions";

export interface ISelectionOptionHeaderProps {
    disabled: boolean;
    isParsingProductUrl: boolean;
    hasDeletePermission: boolean;
    hasUpdatePermission: boolean;
    isCreating: boolean;
    isEditing: boolean;
    isModal?: boolean;
    isExistingEntity: boolean;
    jobId: number;
    jobName: string;
    onBack: () => void;
    onCancel: () => void;
    onDelete: () => void;
    onEdit: () => void;
    onSave: () => void;
    statusMessage: string;
    statusTestId: string;
    statusType: TBTStatusBadgeDisplayTypes;
    /** Raw, editable title value bound to the form (typed into by the user). */
    titleValue: string;
    /** True when the title field should render as read-only static text (view state). */
    titleReadOnly: boolean;
    onTitleChange: (field: string, value: string) => void;
    onTitleBlur: (field: string, touched: boolean, value: string | undefined) => void;
    /** Required-title validation error, shown under the title once touched; undefined hides it. */
    titleError: string | undefined;
    builderInfo: BuilderInfo;
    status?: number;
    onApprove?: () => void;
    onDecline?: () => void;
    onReset?: () => void;
    onSend?: () => void;
    onCopy: () => void;
    canCopy: boolean;
    canSaveToCatalog: boolean;
    onSaveToCatalog: () => void;
    isSavingToCatalog: boolean;
    canCreatePurchaseOrder?: boolean;
    canAddToExistingPurchaseOrder?: boolean;
    onOpenPurchaseOrderLinkingWizard: (toExisting: boolean) => void;
    sharing?: ISharingConfig;
    /** Comment-drawer toggle state. When omitted, the comments button is not rendered. */
    comments?: {
        hasComments: boolean;
        hasUnreadComments: boolean;
        isDrawerOpen: boolean;
        onClick: () => void;
        disabled?: boolean;
    };
    /** Activity-log toggle state. When omitted, the activity button is not rendered. */
    activityLog?: {
        isOpen: boolean;
        onToggle: () => void;
    };
    /** When set, Send/Approve/Decline are disabled and show this as a hover tooltip. */
    coBlockMessage?: string | null;
    /**
     * Intercepts a breadcrumb link click so the host can run its unsaved-changes guard before
     * navigating to `to`. The handler should `preventDefault()` and prompt when the form is dirty,
     * or do nothing (let the link navigate) when it is clean.
     */
    onBreadcrumbNavigate?: (event: MouseEvent<HTMLAnchorElement>, to: string) => void;
}

export function SelectionOptionHeader(props: Readonly<ISelectionOptionHeaderProps>) {
    const userInfo = useContext(UserInfoContext);

    // Breadcrumbs are a full-page navigation affordance, so they're shown only on the standalone
    // details page: when there's a job to link to (the catalog side panel has jobId 0) AND we're
    // not in a modal (a modal is dismissed via its close X, not by navigating away).
    const showBreadcrumb = props.jobId > 0 && !props.isModal;

    // Breadcrumb destinations. Kept as consts so the link's `to` (default navigation) and its
    // `onClick` guard (which navigates to the same target on confirm) can't drift apart.
    const jobBreadcrumbTo = routes.jobPicker.selectJobLink(
        props.jobId,
        routes.landingPage.getLinkForLoginType(userInfo?.loginType ?? BTLoginTypes.BUILDER, true),
    );
    const selectionsBreadcrumbTo = routes.selection.getListLink();

    // In a modal/panel the item is dismissed via a left-anchored close (X) that takes the
    // back arrow's place; on the full-page route it's a back arrow. The two are mutually
    // exclusive, and both are suppressed inside a webview (native back button already present).
    const showModalClose = !!props.isModal && !mobile.isWebview;
    const shouldShowBackLink = !props.isModal && !mobile.isWebview;

    const { primaryAction, secondaryActions, menuActions } = useSelectionOptionHeaderActions({
        disabled: props.disabled,
        isParsingProductUrl: props.isParsingProductUrl,
        hasDeletePermission: props.hasDeletePermission,
        hasUpdatePermission: props.hasUpdatePermission,
        isCreating: props.isCreating,
        isEditing: props.isEditing,
        isExistingEntity: props.isExistingEntity,
        isModal: props.isModal,
        onCancel: props.onCancel,
        onDelete: props.onDelete,
        onEdit: props.onEdit,
        onSave: props.onSave,
        status: props.status,
        onApprove: props.onApprove,
        onDecline: props.onDecline,
        onReset: props.onReset,
        onSend: props.onSend,
        onCopy: props.onCopy,
        canCopy: props.canCopy,
        canSaveToCatalog: props.canSaveToCatalog,
        onSaveToCatalog: props.onSaveToCatalog,
        isSavingToCatalog: props.isSavingToCatalog,
        canCreatePurchaseOrder: props.canCreatePurchaseOrder,
        canAddToExistingPurchaseOrder: props.canAddToExistingPurchaseOrder,
        openPurchaseOrderLinkingWizard: props.onOpenPurchaseOrderLinkingWizard,
        coBlockMessage: props.coBlockMessage,
    });

    // Ancillary icon actions (Share, Comments/Activity, Info) only show for a saved option
    // that has a status — matching the previous slotTop gate.
    const showAncillaryActions = props.isExistingEntity && props.status != null;

    // The header's icon toolbar (Share, Activity, Comments). Activity is a mapped icon; Share is a
    // custom link button (the "copy share link" affordance keeps the link icon rather than the
    // mapped forward/share arrow, per design direction). Both stay inline while there's room and
    // roll up under width pressure (BTOverflow's width-based auto-collapse — no fixed breakpoint).
    // Comments uses the first-class CommentButton, which BTItemHeader keeps pinned inline. The
    // menu-only workflow actions (Approve/Decline/Copy/Delete/Save to catalog) come via `menuActions`.
    const iconActions: TBTItemHeaderIcon[] = [
        {
            icon: "shareLink",
            ariaLabel: "Copy share link",
            isHidden: !showAncillaryActions || !canShare(props.sharing),
            popoverContent: <EntityCopyLinkModalContent sharing={props.sharing} />,
        },
        ...(props.activityLog
            ? [
                  {
                      icon: "activity" as const,
                      ariaLabel: "Toggle activity panel",
                      isHidden: !showAncillaryActions,
                      onClick: props.activityLog.onToggle,
                  },
              ]
            : []),
        ...(props.comments
            ? [
                  {
                      type: "customComponent" as const,
                      id: "selectionOptionComments",
                      isHidden: !showAncillaryActions,
                      customComponent: (
                          <BTItemHeader.CommentButton
                              hasComments={props.comments.hasComments}
                              hasUnreadComments={props.comments.hasUnreadComments}
                              isCommentDrawerOpen={props.comments.isDrawerOpen}
                              onCommentsClick={props.comments.onClick}
                              disabled={props.comments.disabled}
                          />
                      ),
                  },
              ]
            : []),
    ];

    // Title + status badge — the shared body of DetailFrame.Title in both the with-breadcrumb
    // (job) and no-breadcrumb (catalog panel) layouts below. A read-only modal/panel renders the
    // title as a prominent BdsText heading (the panel pattern). Everywhere else — the editable
    // states and the read-only details page — it's the default item-header text area, whose
    // read-only display sits on the same baseline as the status badge.
    const showModalReadOnlyHeading = props.titleReadOnly && !!props.isModal;
    const titleError = !props.titleReadOnly ? props.titleError : undefined;
    const titleRowContent = (
        <>
            <div className={props.titleReadOnly ? "max-w-full" : "w-full"}>
                {showModalReadOnlyHeading ? (
                    <BdsText
                        tag="h1"
                        size="distinct-lg"
                        testid="selectionOptionHeaderTitle"
                        text={props.titleValue}
                    />
                ) : props.isParsingProductUrl ? (
                    <div data-testid="titleSkeleton">
                        <Skeleton active title={false} paragraph={{ rows: 1 }} />
                    </div>
                ) : (
                    <>
                        <BTItemHeader.TextArea
                            id="title"
                            data-testid="selectionOptionHeaderTitle"
                            value={props.titleValue}
                            onChange={props.onTitleChange}
                            onBlur={props.onTitleBlur}
                            readOnly={props.titleReadOnly}
                            minRows={1}
                            maxRows={3}
                            maxLength={100}
                            placeholder="Add selection option title"
                            aria-label="Selection option title"
                            aria-describedby={
                                titleError !== undefined ? "selectionOptionTitleError" : undefined
                            }
                            aria-placeholder="Add selection option title"
                        />
                        {titleError !== undefined && (
                            <BTText
                                id="selectionOptionTitleError"
                                testid="selectionOptionHeaderTitleError"
                                size="normal-sm"
                                className="text-fg-error"
                                text={titleError}
                            />
                        )}
                    </>
                )}
            </div>
            {props.isExistingEntity && props.statusMessage && (
                <BTItemHeader.Metadata
                    badgeText={props.statusMessage}
                    badgeDisplayType={props.statusType}
                    testid={props.statusTestId}
                />
            )}
        </>
    );

    return (
        // The header renders standalone (into a BdsLayout page header slot or a modal header
        // slot), not inside a full DetailFrame page shell. `DetailFrame.FixedActionBar` supplies
        // the canonical fixed-header padding for both the full-page and modal/panel presentations.
        <DetailFrame.FixedActionBar className="selection-option-header pb-0! lg:pb-0!">
            <DetailFrame.ActionButtons
                testid="selectionOptionHeader"
                backLinkOnClick={shouldShowBackLink ? props.onBack : undefined}
                closeOnClick={showModalClose ? props.onBack : undefined}
                primaryAction={primaryAction}
                secondaryActions={secondaryActions}
                iconActions={iconActions}
                menuActions={menuActions}
            />
            {showBreadcrumb ? (
                <DetailFrame.Title
                    testid="selectionOptionHeaderTitleRow"
                    ariaLabel="Selection option header"
                    className="pt-3 pb-0!"
                    breadcrumbTestid="selectionOptionBreadcrumb"
                    breadcrumbSlot={
                        <>
                            <BTBreadcrumb.Item testid="job-link">
                                <BdsLink
                                    testid="bdslink-job-link"
                                    to={jobBreadcrumbTo}
                                    onClick={(event) =>
                                        props.onBreadcrumbNavigate?.(event, jobBreadcrumbTo)
                                    }
                                >
                                    {props.jobName}
                                </BdsLink>
                            </BTBreadcrumb.Item>
                            <BTBreadcrumb.Item testid="selections-breadcrumb">
                                <BdsLink
                                    testid="bdslink-selections-link"
                                    to={selectionsBreadcrumbTo}
                                    onClick={(event) =>
                                        props.onBreadcrumbNavigate?.(event, selectionsBreadcrumbTo)
                                    }
                                >
                                    Selections
                                </BdsLink>
                            </BTBreadcrumb.Item>
                        </>
                    }
                >
                    {titleRowContent}
                </DetailFrame.Title>
            ) : (
                // Breadcrumbs omitted for the modal quick-add and the catalog side panel (no job).
                // DetailFrame.Title's breadcrumb slot is all-or-nothing.
                <DetailFrame.Title
                    testid="selectionOptionHeaderTitleRow"
                    ariaLabel="Selection option header"
                    className="pt-3 pb-0!"
                >
                    {titleRowContent}
                </DetailFrame.Title>
            )}
        </DetailFrame.FixedActionBar>
    );
}

export default SelectionOptionHeader;
